"use client";
import type { ReactNode } from "react";
import { useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import { DotcomAuthProvider } from "@jetblue/dotcom-auth";
import type { Config } from "@jetblue/dotcom-config";

export type AuthBootstrapProperties = {
  readonly authConfig: Config["AUTH"];
  readonly children: ReactNode;
  readonly isLoginCallbackPage?: boolean;
};

const readCookie = (name: string): string | undefined => {
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const entry = cookies.find((cookie) => cookie.startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.slice(name.length + 1)) : undefined;
};

// Client snapshot: safe to read `document.cookie` here, only ever called in
// the browser by `useSyncExternalStore`.
const isAuthKitEnabledForClient = (authConfig: Config["AUTH"]): boolean => {
  if (!authConfig.USE_KIT_AUTH) {
    return false;
  }
  if (!authConfig.IS_AUTH_KIT_COOKIE_ENABLED) {
    return true;
  }
  return (
    readCookie(authConfig.AUTH_KIT_COOKIE_NAME) ===
    authConfig.AUTH_KIT_COOKIE_VALUE
  );
};

// Server snapshot: used for SSR/ISR prerendering and the very first client
// render (pre-hydration), so it must never depend on `document`/cookies.
// When cookie-based rollout gating is active we conservatively fall back to
// the legacy auth flow until the real client snapshot above takes over,
// which keeps the surrounding route fully static/ISR-eligible.
const isAuthKitEnabledForStaticSnapshot = (
  authConfig: Config["AUTH"],
): boolean => authConfig.USE_KIT_AUTH && !authConfig.IS_AUTH_KIT_COOKIE_ENABLED;

// The rollout cookie is set once by Fastly per-request and doesn't change
// during the component's lifetime, so no subscription is needed.
const noopUnsubscribe = (): void => undefined;
const subscribe = (): (() => void) => noopUnsubscribe;

export const AuthBootstrap = ({
  authConfig,
  children,
  isLoginCallbackPage = false,
}: Readonly<AuthBootstrapProperties>) => {
  const router = useRouter();
  const isAuthKitEnabled = useSyncExternalStore(
    subscribe,
    () => isAuthKitEnabledForClient(authConfig),
    () => isAuthKitEnabledForStaticSnapshot(authConfig),
  );

  // AuthKit is off at login callback page, so there's nothing to render here.
  if (isLoginCallbackPage && !isAuthKitEnabled) {
    router.replace("/");
    return null;
  }

  return (
    <DotcomAuthProvider
      authConfig={{ ...authConfig, USE_KIT_AUTH: isAuthKitEnabled }}
    >
      {children}
    </DotcomAuthProvider>
  );
};
